import { SESSION_STORAGE_KEYS } from "repoV2/constants/common/storage/sessionStorage";
import { isBrowser } from "repoV2/utils/common/render/window";
import { setSessionStorageItem } from "repoV2/utils/common/storage/getterAndSetters";
import { makePaymentViaTazapay } from "repoV2/utils/payment/Tazapay/makePaymentViaTazapay";
import { makePaymentViaStripe } from "repoV2/utils/payment/Stripe/makePaymentViaStripe";
import { appendURLSearchParams } from "repoV2/utils/urls&routing/urlParams";
import { getReturnUrl } from "repoV2/utils/payment/getReturnUrl";
import { isRequestSuccessful } from "repoV2/utils/common/api/api";
import { TRANSACTION_STATUSES } from "repoV2/constants/payment";
import { GATEWAYS_ENUM } from "../Payments.constants";
import {
    IInitiateBookingApiResponse,
    ICallPaymentGatewayHOCArgs,
} from "../Payments.interface";

/**
 * This utility returns a function that calls the payment gateway on the response of the callInitiateBookingApi function.
 * @param object {
 *      eventData - Object containing uuid of the listing - used in case of free booking
 *      returnUrl - The url to which the user will be redirected to after payment flow, this is created by
 *      the F.E. for all cases of payment value
 * }
 */
export const callPaymentGatewayHOC =
    ({
        listingDetails,
        returnUrl,
        optionalCallbacks = {},
    }: ICallPaymentGatewayHOCArgs) =>
    ({ response }: { response: IInitiateBookingApiResponse }) => {
        // TODO figure out why this is needed
        if (!isBrowser()) return;

        // This callback is called right after calling payment gateway.
        // Executed for all gateways which dont redirect to a new url,
        // rather load the paymnet UI in the same screen.
        const samePageGatewayLoadCallback =
            optionalCallbacks?.samePageGatewayLoadCallback || (() => {});

        const {
            message,
            status,
            data: {
                options,
                gateway,
                tid: transactionID,
                redirect_url: freeListingRedirectUrl = "", // Incase creator wants its users to be redirected to a specific url incase of the
                // free listings, then we redirect them to this url
                // freeListingRedirectUrl gets priority over returnUrl
                order,
                client_secret: clientSecret, // client_secret is needed for stripe

                response: internalResponse, // internalResponse is needed for tazapay
            },
        } = response;

        if (!isRequestSuccessful(status)) {
            throw new Error(message || "An error occurred. Please try again.");
        }
        if (!gateway) {
            throw new Error("Gateway not specified");
        }

        setSessionStorageItem(
            SESSION_STORAGE_KEYS.PAYMENT_STATUS_GATEWAY_EXISTS,
            true
        );
        setSessionStorageItem(
            SESSION_STORAGE_KEYS.PAYMENT_PAGE_REFERRER,
            window.location.href
        );
        switch (gateway.toLowerCase()) {
            case GATEWAYS_ENUM.RAZORPAY.toLowerCase():
                // eslint-disable-next-line no-case-declarations
                const rzp = new window.Razorpay({
                    ...options,
                    modal: {
                        ondismiss: () => {
                            // TODO figure out why this is needed
                            optionalCallbacks?.onRazorpayClose?.();
                        },
                    },
                });
                rzp?.open();
                samePageGatewayLoadCallback();
                break;

            // This ensures that the Juspay API is loaded and calling the SDK doesn't throw an error
            case GATEWAYS_ENUM.NEW_JUSPAY.toLowerCase(): {
                // getting web as weblink to redirect coming from backend
                if (order?.web) window.location.href = order?.web;
                break;
            }

            // This ensures that the Tazapay API is loaded and calling the SDK doesn't throw an error
            case GATEWAYS_ENUM.TAZAPAY.toLowerCase(): {
                makePaymentViaTazapay(internalResponse);
                break;
            }

            case GATEWAYS_ENUM.STRIPE.toLowerCase():
                // This event trigger tells the stripe component to show itself and collect the user's card
                makePaymentViaStripe(clientSecret);
                samePageGatewayLoadCallback();
                break;

            case GATEWAYS_ENUM.FREE.toLowerCase(): {
                const url =
                    freeListingRedirectUrl ||
                    appendURLSearchParams(
                        (returnUrl ||
                            getReturnUrl({
                                isDirectBooking: false,
                                eventId: listingDetails?.uuid,
                            }))!,
                        {
                            transaction: transactionID,
                            status: TRANSACTION_STATUSES.SUCCESS,
                        }
                    );
                window.open(url, "_self"); // TODO: Use Router instead here?
                break;
            }
            default:
                break;
        }
    };
